import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Breadcrumb from "@components/UI/Breadcrumb";
import CustomInput from "@components/Forms/CustomInput";
import CustomSelect from "@components/UI/CustomSelect";
import ToggleSwitch from "@components/Forms/ToggleSwitch";
import ShowPasswordIcon from "@components/Icons/ShowPasswordIcon";
import HidePasswordIcon from "@components/Icons/HidePasswordIcon";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomCheckbox, CustomDatePicker } from "@components/Forms";
import ToastConstant from "@configs/react-toastify/snackbar";
import { CHARACTER_LENGTH, TOAST } from "@constants/index";
import validator from 'validator';
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { useMaskedPasswordCaret } from "@hooks/useMaskedPasswordCaret";
import { getScanToolHelper, translateDepartment } from "@utils/common";

const AddUser = () => {
  const navigate = useNavigate();
  const isFirstRender = useRef(true);
  const { colors } = useTheme();
  const { t } = useTranslation();

  const {
    inputRef: passwordRef,
    handleKeyDown: handlePasswordKeyDown,
  } = useMaskedPasswordCaret();

  const {
    inputRef: confirmRef,
    handleKeyDown: handleConfirmKeyDown,
  } = useMaskedPasswordCaret();

  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    role: "",
    expirationDt: null,
    enableFlag: true,
    department: "",
    position: "",
    email: "",
    phoneNo: "",
    cellNo: "",
    binaryVerificationMailFlag: false,
    cveDetectionMailFlag: false,
    securityAlertMailFlag: false,
    sourceCodeVerificationMailFlag: false,
    sbomVerificationMailFlag: false,
    clarityAirVerificationMailFlag: false,
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { assignableRolesList, departmentList, scanToolList, fetchSearchInfo } = useSearchInfo();
  const { hasTool } = getScanToolHelper(scanToolList);
  const departmentOptions = (departmentList || []).map(item => ({
    value: item.value,
    label: translateDepartment(t, item.value),
  }));

  const breadcrumbItems = [
    { label: t("system.user.user-list"), href: "/user" },
    { label: t("system.user.create-new-user"), href: "/user/new" },
  ];

  // Create User API
  const createUser = useMutationData(
    queryKeys.registerUser,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.USERS, response, response.userId);
        ToastConstant(TOAST.success, t("system.user.user-created-success"));
        setLoading(false);
        navigate("/user");
      },
      onError: (error) => {
        const errorMsg =
          error.response?.status === 400 && error.response?.data
            ? error.response.data
            : "";
        let subErrMsg = "";
        switch (errorMsg) {
          case "User has reached limit":
            subErrMsg = t("system.user.add-error-limit");
            break;
          case "User already existed":
            subErrMsg = t("system.user.add-error-existed");
            break;
          case "Duplicate email found":
            subErrMsg = t("system.user.add-error-email-duplicate");
            break;
          default:
            subErrMsg = "";
        }
        ToastConstant(TOAST.error, t("system.user.add-error"), subErrMsg);
        setLoading(false);
      },
    },
  );

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSearchInfo();
      isFirstRender.current = false;
    }
  }, [fetchSearchInfo]);

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCancel = () => {
    navigate("/user");
  };

  const isValidEmail = (email) => validator.isEmail(email);
  const isValidPhoneCellNo = (number) => validator.isMobilePhone(number);

  const validateForm = () => {
    const baseValid =
      formData.userId?.trim() &&
      formData.userName?.trim() &&
      formData.role &&
      formData.password?.trim() &&
      formData.confirmPassword?.trim() &&
      formData.password === formData.confirmPassword;
    const emailValid = formData.email?.trim() && isValidEmail(formData.email);
    return baseValid && emailValid;
  };

  const isFormValid = () => {
    return validateForm();
  };

  const handleSave = async () => {
    if (!formData.email?.trim() || !isValidEmail(formData.email)) {
      ToastConstant(TOAST.error, t("system.user.email-required", "Email is required and must be valid"));
      return;
    }

    if (!isValidPhoneCellNo(formData.phoneNo) && formData.phoneNo?.trim()) {
      return;
    }

    if (!isValidPhoneCellNo(formData.cellNo) && formData.cellNo?.trim()) {
      return;
    }

    if (formData.password.trim().length < 6) {
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const bodyRequest = {
      userId: formData.userId?.trim() || "",
      userName: formData.userName?.trim() || "",
      roleCd: formData.role,
      expirationDt: formData.expirationDt,
      enableFlag: formData.enableFlag ? "Y" : "N",
      deptCd: formData.department || "",
      position: formData.position || "",
      email: formData.email || "",
      phoneNo: formData.phoneNo || "",
      cellNo: formData.cellNo || "",
      userMailConfig: {
        binaryVerificationMailFlag: formData.binaryVerificationMailFlag ? "Y" : "N",
        cveDetectionMailFlag: formData.cveDetectionMailFlag ? "Y" : "N",
        securityAlertMailFlag: formData.securityAlertMailFlag ? "Y" : "N",
        sourceCodeVerificationMailFlag: formData.sourceCodeVerificationMailFlag ? "Y" : "N",
        sbomVerificationMailFlag: formData.sbomVerificationMailFlag ? "Y" : "N",
        clarityAirVerificationMailFlag: formData.clarityAirVerificationMailFlag ? "Y" : "N",
      },
      password: formData.password || "",
    };

    await createUser.mutateAsync(bodyRequest);
  };

  return (
    <div data-testid="add-user-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`}>
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className={`${colors.textPrimary} text-2xl font-bold uppercase`}>
              {t("system.user.add-new-user")}
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <OutlineButton data-testid="add-user-cancel-button" onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton data-testid="add-user-save-button" onClick={handleSave} disabled={!isFormValid() || loading}>
              {loading ? (
                <div className="flex items-center">
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  {t("common.saving")}
                </div>
              ) : (
                t("common.save-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div data-testid="add-user-form" className={`mt-3 p-6 ${colors.bgPrimary} rounded-2xl`}>
        <div className={`mx-auto max-w-[55rem]`}>
          <div>
            {/* User Information Section */}
            <div className="mb-6">
              <h2 className={`${colors.textPrimary} text-xl font-semibold mb-4 uppercase`}>
                {t("system.user.user-information")}
              </h2>

              <div className="grid grid-cols-3 gap-5">
                {/* User ID */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.user-id")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid="add-user-user-id-input"
                    type="text"
                    value={formData.userId}
                    onChange={(e) => handleInputChange("userId", e.target.value)}
                    placeholder={t("system.user.user-id-placeholder")}
                    className="w-full"
                    maxLength={100}
                  />
                </div>

                {/* User Name */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.user-name")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid="add-user-user-name-input"
                    type="text"
                    value={formData.userName}
                    onChange={(e) => handleInputChange("userName", e.target.value)}
                    placeholder={t("system.user.user-name-placeholder")}
                    className="w-full"
                    maxLength={CHARACTER_LENGTH.TITLE}
                  />
                </div>

                {/* Role */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.role")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomSelect
                    options={assignableRolesList}
                    value={assignableRolesList.find((opt) => opt.value === formData.role)}
                    onChange={(opt) => handleInputChange("role", opt?.value || null)}
                    placeholder={t("system.user.role-placeholder")}
                    className="w-full text-sm font-medium"
                  />
                </div>

                {/* Expiration Date */}
                <div className="col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.expiration-date")}
                  </label>
                  <CustomDatePicker
                    selected={formData.expirationDt}
                    onChange={(date) => handleInputChange("expirationDt", date)}
                    placeholderText={t("system.user.expiration-date")}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                  />
                </div>

                {/* User Status */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.user-status")}
                  </label>
                  <div className="h-10 flex items-center space-x-3">
                    <ToggleSwitch
                      checked={formData.enableFlag}
                      onChange={(checked) => handleInputChange("enableFlag", checked)}
                    />
                    <span className={`text-sm ${colors.textPrimary}`}>
                      {formData.enableFlag ? t("system.user.enable", "Enable") : t("system.user.disable", "Disable")}
                    </span>
                  </div>
                </div>

                {/* Department */}
                {/* <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.department")}
                  </label>
                  <CustomSelect
                    options={departmentOptions}
                    value={departmentOptions.find((opt) => opt.value === formData.department)}
                    onChange={(opt) => handleInputChange("department", opt?.value || null)}
                    placeholder={t("system.user.department-placeholder")}
                    className="w-full text-sm font-medium"
                  />
                </div> */}

                {/* Position */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.position")}
                  </label>
                  <CustomInput
                    type="text"
                    value={formData.position}
                    onChange={(e) => handleInputChange("position", e.target.value)}
                    placeholder={t("system.user.position-placeholder")}
                    className="w-full"
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.email")}
                    <span className={colors.textRequired}> *</span>
                  </label>
                  <CustomInput
                    data-testid="add-user-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder={t("system.user.email-placeholder")}
                    className="w-full"
                  />
                  {formData.email && !isValidEmail(formData.email) && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>{t("system.email-invalid")}</div>
                  )}
                </div>

                {/* Phone No. */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.phone-no")}
                  </label>
                  <CustomInput
                    type="tel"
                    value={formData.phoneNo}
                    onChange={(e) => handleInputChange("phoneNo", e.target.value)}
                    placeholder={t("system.user.phone-no-placeholder")}
                    className="w-full"
                  />
                  {formData.phoneNo && !isValidPhoneCellNo(formData.phoneNo) && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>{t("system.phone-no-invalid")}</div>
                  )}
                </div>

                {/* Cell No. */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.cell-no")}
                  </label>
                  <CustomInput
                    type="tel"
                    value={formData.cellNo}
                    onChange={(e) => handleInputChange("cellNo", e.target.value)}
                    placeholder={t("system.user.cell-no-placeholder")}
                    className="w-full"
                  />
                  {formData.cellNo && !isValidPhoneCellNo(formData.cellNo) && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>{t("system.cell-no-invalid")}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Mail Reception Section */}
            <div className="mb-6">
              <h2 className={`${colors.textPrimary} text-xl font-semibold mb-4 uppercase`}>
                {t("system.user.mail-reception", "Mail Reception")}
              </h2>
              <div className="flex flex-col space-y-3">
                {hasTool("clarity") && <CustomCheckbox
                  id="binaryVerificationMailFlag"
                  checked={formData.binaryVerificationMailFlag}
                  onChange={(e) => handleInputChange("binaryVerificationMailFlag", e.target.checked)}
                  label={t("system.user.clarity-verif-mail-reception", "Clarity Verif. Mail Reception")}
                />}
                <CustomCheckbox
                  id="cveDetectionMailFlag"
                  checked={formData.cveDetectionMailFlag}
                  onChange={(e) => handleInputChange("cveDetectionMailFlag", e.target.checked)}
                  label={t("system.user.cve-detection-mail-reception")}
                />
                <CustomCheckbox
                  id="securityAlertMailFlag"
                  checked={formData.securityAlertMailFlag}
                  onChange={(e) => handleInputChange("securityAlertMailFlag", e.target.checked)}
                  label={t("system.user.security-alert-mail-reception", "Security Alert")}
                />
                {hasTool("fossid") && <CustomCheckbox
                  id="sourceCodeVerificationMailFlag"
                  checked={formData.sourceCodeVerificationMailFlag}
                  onChange={(e) => handleInputChange("sourceCodeVerificationMailFlag", e.target.checked)}
                  label={t("system.user.fossid-verif-mail-reception", "FossID Verif. Mail Reception")}
                />}
                {hasTool("sbom") && <CustomCheckbox
                  id="sbomVerificationMailFlag"
                  checked={formData.sbomVerificationMailFlag}
                  onChange={(e) => handleInputChange("sbomVerificationMailFlag", e.target.checked)}
                  label={t("system.user.sbom-verif-mail-reception", "SBOM Verif. Mail Reception")}
                />}
                {hasTool("clarityair") && <CustomCheckbox
                  id="clarityAirVerificationMailFlag"
                  checked={formData.clarityAirVerificationMailFlag}
                  onChange={(e) => handleInputChange("clarityAirVerificationMailFlag", e.target.checked)}
                  label={t("system.user.clarity-air-verif-mail-reception", "ClarityAIR Verif. Mail Reception")}
                />}
              </div>
            </div>

            {/* Password Section */}
            <div className="mb-3">
              <h2 className={`${colors.textPrimary} text-xl font-semibold mb-2 uppercase`}>
                {t("system.user.password")}
              </h2>
              <div className="grid grid-cols-2 gap-5">
                {/* Password */}
                <div>
                  <label className={`mb-0.5 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.password", "Password")} <span className={colors.textRequired}>*</span>
                  </label>
                  <div className="relative">
                    <CustomInput
                      data-testid="add-user-password-input"
                      ref={passwordRef}
                      className="w-full pr-10"
                      type="text"
                      autoComplete="off"
                      placeholder={t("system.user.password-placeholder")}
                      value={
                        showPassword
                          ? formData.password
                          : "•".repeat(formData.password.length)
                      }
                      onChange={(e) => {
                        if (showPassword) {
                          handleInputChange("password", e.target.value);
                        }
                      }}
                      onKeyDown={(e) =>
                        handlePasswordKeyDown(
                          e,
                          formData.password,
                          (v) => handleInputChange("password", v),
                          showPassword
                        )
                      }
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute top-0 right-0 h-10 pr-3 flex items-center justify-center"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <HidePasswordIcon
                          className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                        />
                      ) : (
                        <ShowPasswordIcon
                          className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                        />
                      )}
                    </button>
                  </div>
                  {formData.password.length > 0 && formData.password.length < 6 && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>
                      {t("system.user.password-min-length", "Password must be at least 6 characters")}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className={`mb-0.5 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("system.user.confirm-password", "Confirm Password")} <span className={colors.textRequired}>*</span>
                  </label>
                  <div className="relative">
                    <CustomInput
                      ref={confirmRef}
                      className="w-full pr-10"
                      type="text"
                      autoComplete="off"
                      placeholder={t("system.user.confirm-password-placeholder", "Confirm Password")}
                      value={
                        showConfirmPassword
                          ? formData.confirmPassword
                          : "•".repeat(formData.confirmPassword.length)
                      }
                      onChange={(e) => {
                        if (showConfirmPassword) {
                          handleInputChange("confirmPassword", e.target.value);
                        }
                      }}
                      onKeyDown={(e) =>
                        handleConfirmKeyDown(
                          e,
                          formData.confirmPassword,
                          (v) => handleInputChange("confirmPassword", v),
                          showConfirmPassword
                        )
                      }
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute top-0 right-0 h-10 pr-3 flex items-center justify-center"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <HidePasswordIcon
                          className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                        />
                      ) : (
                        <ShowPasswordIcon
                          className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                        />
                      )}
                    </button>
                  </div>
                  {formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>
                      {t("system.user.password-mismatch", "Password does not match")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
